import React from 'react';
import {
    Search,
    ArrowRight,
    Calendar,
    Clock,
    Heart,
    Monitor,
    BookOpen,
    Users,
    Megaphone,
    Mail,
    Send
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';

export default function BlogPage() {
    const recentArticles = [
        {
            id: 1,
            badge: "EXCELLENCE CLIENT",
            badgeBg: "bg-blue-600",
            image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=600",
            date: "7 mai 2024",
            readTime: "6 min de lecture",
            title: "5 stratégies pour améliorer la satisfaction client en 2024",
            excerpt: "Des actions concrètes pour dépasser les attentes de vos clients et les fidéliser durablement.",
        },
        {
            id: 2,
            badge: "TECHNOLOGIE",
            badgeBg: "bg-blue-600",
            image: "https://images.unsplash.com/photo-1556745757-8d76bdb6984b?auto=format&fit=crop&q=80&w=600",
            date: "2 mai 2024",
            readTime: "5 min de lecture",
            title: "WhatsApp Business : un canal incontournable pour le support",
            excerpt: "Comment tirer le meilleur parti de WhatsApp pour communiquer efficacement avec vos clients.",
        },
        {
            id: 3,
            badge: "GUIDE",
            badgeBg: "bg-blue-600",
            image: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&q=80&w=600",
            date: "28 avril 2024",
            readTime: "7 min de lecture",
            title: "Les KPI indispensables pour mesurer la performance de votre service client",
            excerpt: "Les indicateurs clés pour suivre, analyser et améliorer vos performances au quotidien.",
        },
        {
            id: 4,
            badge: "GESTION D'ÉQUIPE",
            badgeBg: "bg-blue-600",
            image: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=600",
            date: "20 avril 2024",
            readTime: "6 min de lecture",
            title: "Motiver votre équipe support : bonnes pratiques et astuces",
            excerpt: "Des conseils simples et efficaces pour stimuler l'engagement et la productivité de vos agents.",
        },
    ];

    const categories = [
        { icon: Heart, name: "Excellence client", count: 18, color: "text-red-500" },
        { icon: Monitor, name: "Technologie", count: 15, color: "text-blue-500" },
        { icon: BookOpen, name: "Guide & Tutoriels", count: 12, color: "text-amber-500" },
        { icon: Users, name: "Gestion d'équipe", count: 10, color: "text-emerald-500" },
        { icon: Megaphone, name: "Actualités", count: 8, color: "text-orange-500" },
    ];

    return (
        <PublicPageLayout>
                <div className="px-6 lg:px-12 py-6 mx-auto space-y-10">

                    {/* ================= HERO SECTION & FEATURED ARTICLE ================= */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">

                        {/* Gauche: Titre du blog & Barre de recherche */}
                        <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
                            <div className="space-y-4">
                                <span className="text-xs font-bold text-blue-600 tracking-wider uppercase">
                                    BLOG
                                </span>
                                <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight">
                                    Des ressources et des conseils pour offrir une expérience client <span className="text-blue-600">exceptionnelle.</span>
                                </h1>
                                <p className="text-slate-500 text-sm leading-relaxed max-w-md">
                                    Découvrez nos articles, guides et actualités pour rester à jour sur les meilleures pratiques en relation client et en technologie.
                                </p>
                            </div>

                            {/* Barre de recherche */}
                            <div className="flex items-center gap-2 pt-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input
                                        type="text"
                                        placeholder="Rechercher un article..."
                                        className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm outline-none focus:border-blue-500 transition-colors shadow-sm"
                                    />
                                </div>
                                <button className="bg-blue-600 text-white font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors shadow-sm">
                                    Rechercher
                                </button>
                            </div>
                        </div>

                        {/* Droite: Article À LA UNE */}
                        <div className="lg:col-span-7 relative rounded-3xl overflow-hidden shadow-xl min-h-[340px] flex flex-col justify-end p-8 text-white group">
                            {/* Image de fond */}
                            <img
                                src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=1200"
                                alt="À la une"
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            {/* Overlays sombres */}
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-900/40 to-transparent" />

                            {/* Contenu sur l'image */}
                            <div className="relative z-10 space-y-3 max-w-xl">
                                <div className="flex items-center gap-3 text-xs font-semibold">
                                    <span className="bg-blue-600 text-white px-2.5 py-1 rounded-md uppercase text-[10px] tracking-wider">
                                        À LA UNE
                                    </span>
                                    <span className="text-slate-300">10 mai 2024</span>
                                </div>

                                <h2 className="text-2xl lg:text-3xl font-bold leading-snug">
                                    IA et relation client : comment offrir un service rapide et humain à la fois ?
                                </h2>

                                <p className="text-slate-300 text-xs lg:text-sm line-clamp-2 leading-relaxed">
                                    L'intelligence artificielle transforme la relation client. Découvrez comment l'utiliser pour améliorer l'expérience sans perdre la touche humaine.
                                </p>

                                <button className="pt-2 flex items-center gap-2 text-sm font-semibold text-white hover:text-blue-300 transition-colors">
                                    Lire l'article <ArrowRight size={16} />
                                </button>
                            </div>
                        </div>

                    </div>

                    {/* ================= MAIN CONTENT SECTION (ARTICLES + SIDEBAR) ================= */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                        {/* Colonne Gauche: Articles récents (8 cols) */}
                        <div className="lg:col-span-8 space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-bold text-slate-900">Articles récents</h2>
                                <button className="text-blue-600 hover:text-blue-700 mt-3 text-xs font-bold flex items-center gap-1 transition-colors">
                                    Voir tous les articles <ArrowRight size={14} />
                                </button>
                            </div>

                            {/* Grille des 4 articles récents */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                {recentArticles.map((article) => (
                                    <div key={article.id} className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col justify-between hover:shadow-md transition-shadow">

                                        {/* Visuel + Badge */}
                                        <div className="relative h-44 overflow-hidden">
                                            <img
                                                src={article.image}
                                                alt={article.title}
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                            />
                                            <span className={`absolute bottom-3 left-3 ${article.badgeBg} text-white text-[9px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider`}>
                                                {article.badge}
                                            </span>
                                        </div>

                                        {/* Contenu */}
                                        <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-3 text-[11px] text-slate-400 font-medium">
                                                    <span className="flex items-center gap-1">
                                                        <Calendar size={12} />
                                                        {article.date}
                                                    </span>
                                                    <span>•</span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock size={12} />
                                                        {article.readTime}
                                                    </span>
                                                </div>

                                                <h3 className="font-bold text-slate-900 text-sm leading-snug line-clamp-2 hover:text-blue-600 cursor-pointer transition-colors">
                                                    {article.title}
                                                </h3>

                                                <p className="text-slate-500 text-xs leading-relaxed line-clamp-2">
                                                    {article.excerpt}
                                                </p>
                                            </div>

                                            <button className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-700 transition-colors pt-2">
                                                Lire l'article <ArrowRight size={13} />
                                            </button>
                                        </div>

                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Colonne Droite: Sidebar (4 cols) */}
                        <div className="lg:col-span-4 space-y-6">

                            {/* Widget 1: Catégories */}
                            <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm space-y-4">
                                <h3 className="font-bold text-slate-900 text-base">Catégories</h3>

                                <div className="space-y-1">
                                    {categories.map((cat, idx) => {
                                        const Icon = cat.icon;
                                        return (
                                            <button
                                                key={idx}
                                                className="w-full flex items-center justify-between p-2.5 rounded-xl hover:bg-slate-50 transition-colors text-left group"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Icon size={18} className={cat.color} />
                                                    <span className="text-xs font-semibold text-slate-700 group-hover:text-slate-900">
                                                        {cat.name}
                                                    </span>
                                                </div>
                                                <span className="text-[11px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                                                    {cat.count}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>

                                <button className="text-blue-600 hover:text-blue-700 text-xs font-bold flex items-center gap-1 pt-2 transition-colors">
                                    Voir toutes les catégories <ArrowRight size={13} />
                                </button>
                            </div>

                            {/* Widget 2: Restez informé (Newsletter) */}
                            <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm space-y-4">
                                <div className="space-y-1">
                                    <h3 className="font-bold text-slate-900 text-base">Restez informé</h3>
                                    <p className="text-slate-500 text-xs leading-relaxed">
                                        Abonnez-vous à notre newsletter et recevez nos derniers articles et actualités.
                                    </p>
                                </div>

                                <form onSubmit={(e) => e.preventDefault()} className="space-y-3">
                                    <div className="relative">
                                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                        <input
                                            type="email"
                                            placeholder="Votre adresse e-mail"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs outline-none focus:border-blue-500 focus:bg-white transition-colors"
                                        />
                                    </div>

                                    <button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2">
                                        S'abonner <ArrowRight size={14} />
                                    </button>
                                </form>
                            </div>

                        </div>

                    </div>

                    {/* ================= BANNIÈRE CTA BAS DE PAGE ================= */}
                    <div className="bg-[#031533] rounded-3xl p-6 lg:p-8 text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg">

                        <div className="flex items-center gap-4 text-center md:text-left">
                            <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                                <Send size={22} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-lg lg:text-xl font-bold">
                                    Besoin d'aide pour améliorer votre relation client ?
                                </h3>
                                <p className="text-slate-400 text-xs lg:text-sm mt-0.5">
                                    Nos experts sont là pour vous accompagner dans votre transformation.
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-3 w-full md:w-auto">
                            <button className="border border-slate-600 hover:bg-slate-800 text-white font-semibold text-xs px-4 py-2.5 rounded-xl flex items-center gap-2 transition-colors whitespace-nowrap">
                                Demander une démo <Calendar size={14} />
                            </button>
                            <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs px-5 py-2.5 rounded-xl shadow-sm transition-colors flex items-center gap-2 whitespace-nowrap">
                                Nous contacter <ArrowRight size={14} />
                            </button>
                        </div>

                    </div>

                </div>
        </PublicPageLayout>
    );
}